import {useCallback, useState} from 'react';
import {showToast} from '@chekinapp/ui';
import {FastAPIServiceError} from '@chekin/core';
import {useFinishUserAccountMutation} from 'hooks/auth/useFinishUserAccountMutation';
import {useInitUserAccountMutation} from 'hooks/auth/useInitUserAccountMutation';
import {useAuth} from 'hooks/auth/useAuth';
import {useAuthErrorMessage} from 'hooks/auth/useAuthErrorMessage';

type Props = {
  onSignUpSuccess?: () => void;
  onSignUpError?: (error: FastAPIServiceError) => void;
  onSignUpConfirmationSuccess?: () => void;
  onSignUpConfirmationError?: (error: FastAPIServiceError) => void;
};
export function useSignUp({
  onSignUpSuccess,
  onSignUpError,
  onSignUpConfirmationSuccess,
  onSignUpConfirmationError,
}: Props) {
  const [sessionId, setSessionId] = useState('');
  const getErrorMessage = useAuthErrorMessage();
  const {authenticate} = useAuth();

  const {mutate: signUp, isPending: isSignUpLoading} = useInitUserAccountMutation({
    onSuccess: data => {
      setSessionId(data.session_id);
      onSignUpSuccess?.();
    },
    onError: error => {
      showToast.error(getErrorMessage(error, 'auth.sign_up_error'));
      onSignUpError?.(error);
    },
  });

  const {mutate: finalizeUserAccountMutation, isPending: isSignUpConfirmationLoading} =
    useFinishUserAccountMutation({
      onSuccess: data => {
        authenticate({token: data.access_token});
        onSignUpConfirmationSuccess?.();
      },
      onError: error => {
        showToast.error(getErrorMessage(error, 'auth.sign_up_confirmation_error'));
        onSignUpConfirmationError?.(error);
      },
    });

  const confirmSignUp = useCallback(
    ({code}: {code: string}) => {
      finalizeUserAccountMutation({
        code,
        session_id: sessionId,
      });
    },
    [finalizeUserAccountMutation, sessionId],
  );

  return {
    signUp,
    confirmSignUp,
    isLoading: isSignUpLoading || isSignUpConfirmationLoading,
  };
}
